import { Switch as SwitchPrimitive } from "@base-ui/react/switch";
import { merge } from "./merge";

type SwitchProps = Omit<SwitchPrimitive.Root.Props, "onCheckedChange"> & {
  /** Base UI also passes event details; callers here only ever need the new state. */
  onCheckedChange?: (checked: boolean) => void;
};

/**
 * An on/off setting that takes effect as it is flipped. It needs an accessible name from the
 * caller (`aria-label` or `aria-labelledby`): the track carries no text of its own.
 *
 * Base UI renders the `role="switch"` element plus a hidden input, so form submission and
 * Space/Enter come with it. The thumb travels 16px inside a 36px track with 2px of padding.
 */
export function Switch({ className, onCheckedChange, ...props }: SwitchProps) {
  return (
    <SwitchPrimitive.Root
      {...props}
      onCheckedChange={(checked) => onCheckedChange?.(checked)}
      data-slot="switch"
      className={merge(
        "inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full bg-input p-0.5 outline-none transition-colors duration-fast ease-out focus-visible:ring-3 focus-visible:ring-(--focus-halo) data-checked:bg-foreground data-disabled:cursor-not-allowed data-disabled:opacity-50 motion-reduce:transition-none",
        className,
      )}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className="size-4 rounded-full bg-card shadow-sm transition-transform duration-fast ease-out data-checked:translate-x-4 motion-reduce:transition-none"
      />
    </SwitchPrimitive.Root>
  );
}
